import { useMerchant } from "@/app/providers/merchantProvider";
import { useNativeBridge } from "@/app/providers/nativeBridgeContext";
import { sendMessage } from "@clover-native/message-hub";
import { LocalStorageKey } from "./storageKeys";

export enum CmaWebEvents {
  OPEN_CUSTOMER_PROFILE = "openCustomerProfile",
  OPEN_EXTERNAL_LINK = "openExternalLink",
  CLOSE_WEB_VIEW = "closeWebView",
  CRASH_RELOAD = "onRenderProcessGone",
  ERROR = "error",
  GET_PAYMENT_TOKEN = "getPaymentToken",
  INVOKE_NATIVE_WALLET = "invokeNativeWallet",
  APP_CONTENT_RENDERED = "AppContentRendered",
  GET_SAVED_PAYMENT_CARDS = "getSavedPaymentCards",
  HIDE_WEB_VIEW = "hideWebView",
  OPEN_LOGIN_SIGN_UP = "openLoginSignUp",
  OPEN_ADDRESS_DIALOG = "openAddressDialog",
  SYNC_CART_CONTEXT = "syncCartContext",
  RENEW_SESSION = "renewSession",
  OPEN_NATIVE_ADD_CARD = "openNativeAddCard",
  VIEW_RECEIPT = "viewReceipt",
  GET_SAVED_ADDRESS = "getSavedAddress",
  LEAVE_HOME_PAGE = "leaveHomePage",
  ENTER_HOME_PAGE = "enterHomePage",
}

export const useNativeActions = () => {
  const { sendEvent, source, cmaPlatform } = useNativeBridge();
  const { merchant } = useMerchant();

  const getMerchantType = (): string => {
    if (!merchant) return "unknown";

    const isHybrid =
      merchant.isHybrid || (merchant.isRetail && merchant.isService);

    if (isHybrid) {
      if (merchant.hasOnlyRetailItems) {
        return "hybrid_retail";
      }

      const retailCart = localStorage.getItem(LocalStorageKey.RETAIL_CART);
      const serviceCart = localStorage.getItem(LocalStorageKey.SERVICE_CART);

      const hasItems = (cartStr: string | null) => cartStr && cartStr !== "[]";

      if (hasItems(serviceCart)) {
        return "hybrid_service";
      }

      if (hasItems(retailCart)) {
        return "hybrid_retail";
      }

      return "Hybrid";
    }

    if (merchant.isRestaurant) return "restaurant";
    if (merchant.isService) return "service";
    if (merchant.isRetail) return "retail";

    return "unknown";
  };

  const platform = cmaPlatform?.toLowerCase();

  return {
    merchant,

    getMerchantType,

    isCmaApp: source === "cma",

    isAndroid: platform === "android",

    isIos: platform === "ios",

    openCustomerProfile: () => sendEvent(CmaWebEvents.OPEN_CUSTOMER_PROFILE),

    openExternalLink: (url: string) =>
      sendMessage(CmaWebEvents.OPEN_EXTERNAL_LINK, JSON.stringify({ url })),

    closeWebView: () => sendEvent(CmaWebEvents.CLOSE_WEB_VIEW),

    appContentRendered: () => sendEvent(CmaWebEvents.APP_CONTENT_RENDERED),

    getPaymentToken: (amount: number) =>
      sendEvent(CmaWebEvents.GET_PAYMENT_TOKEN, JSON.stringify({ amount })),

    invokeNativeWallet: () => sendEvent(CmaWebEvents.INVOKE_NATIVE_WALLET),

    getSavedPaymentCards: () => sendEvent(CmaWebEvents.GET_SAVED_PAYMENT_CARDS),

    hideWebView: () => sendEvent(CmaWebEvents.HIDE_WEB_VIEW),

    renewSession: () => sendEvent(CmaWebEvents.RENEW_SESSION),

    openNativeAddCard: () => sendEvent(CmaWebEvents.OPEN_NATIVE_ADD_CARD),

    getSavedAddress: () => sendEvent(CmaWebEvents.GET_SAVED_ADDRESS),

    openAddressDialog: () => sendEvent(CmaWebEvents.OPEN_ADDRESS_DIALOG),

    leaveHomePage: () => sendEvent(CmaWebEvents.LEAVE_HOME_PAGE),

    enterHomePage: () => sendEvent(CmaWebEvents.ENTER_HOME_PAGE),

    viewReceipt: (url: string) =>
      sendMessage(CmaWebEvents.VIEW_RECEIPT, JSON.stringify({ url })),

    openLoginSignUp: (openedFrom: string) => {
      const payload = JSON.stringify({
        merchantType: getMerchantType(),
        openedFrom: openedFrom,
      });
      sendMessage(CmaWebEvents.OPEN_LOGIN_SIGN_UP, payload);
    },

    syncCartContext: (cart: any) => {
      if (!merchant) return;
      sendMessage(
        CmaWebEvents.SYNC_CART_CONTEXT,
        JSON.stringify({
          cartMerchantId: merchant.id,
          cartMerchantSlug: merchant.slug,
          cart: cart,
        })
      );
    },
  };
};
